import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useId, useMemo, useState, type KeyboardEvent } from 'react';

import { cn } from '@/utils/cn';

import type { ListboxOption, ListboxSize, ListboxValue } from './listbox';

const TRIGGER_SIZES: Record<ListboxSize, string> = {
  sm: 'h-8 min-h-8 px-2.5 text-body-sm',
  md: 'min-h-10 px-3 text-body',
  lg: 'h-12 min-h-12 px-4 text-card-title',
  kiosk: 'h-20 min-h-20 px-6 text-kiosk-body',
};
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * Multi-select. Selected values show as removable chips up to a threshold, then
 * collapse to a count — a group with forty members selected must not push the form
 * off the screen.
 */

export interface MultiSelectListboxProps<T extends ListboxValue = string> {
  value: T[];
  onChange: (value: T[]) => void;
  options: ListboxOption<T>[];
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Rendered when more than `maxChips` are selected; receives the count. */
  summaryLabel?: (count: number) => string;
  selectAllLabel?: string;
  clearAllLabel?: string;
  removeLabel?: string;
  maxChips?: number;
  size?: ListboxSize;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export const MultiSelectListbox = <T extends ListboxValue = string>({
  value,
  onChange,
  options,
  placeholder,
  searchable = true,
  searchPlaceholder,
  emptyMessage,
  summaryLabel,
  selectAllLabel,
  clearAllLabel,
  removeLabel,
  maxChips = 3,
  size = 'md',
  disabled = false,
  invalid = false,
  id,
  popoverPortal = 'body',
  className,
  ...aria
}: MultiSelectListboxProps<T>) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();

  const triggerSizeClass = TRIGGER_SIZES[size];

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedOptions = options.filter((option) => selectedSet.has(option.value));

  const visible = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const needle = search.trim().toLowerCase();
    return options.filter((option) =>
      [option.label, option.description, ...(option.keywords ?? [])]
        .filter(Boolean)
        .some((text) => text!.toLowerCase().includes(needle)),
    );
  }, [options, search, searchable]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (next) return;
    setSearch('');
    setActiveIndex(0);
  };

  const toggle = (option: ListboxOption<T>) => {
    if (option.disabled) return;
    // Stays open: choosing five departments should be five clicks, not five
    // reopenings of the menu.
    onChange(
      selectedSet.has(option.value)
        ? value.filter((current) => current !== option.value)
        : [...value, option.value],
    );
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) changeOpen(true);
      else setActiveIndex((current) => Math.min(current + 1, visible.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      const option = visible[activeIndex];
      if (option) toggle(option);
    }
  };

  const selectableVisible = visible.filter((option) => !option.disabled);
  const allVisibleSelected =
    selectableVisible.length > 0 &&
    selectableVisible.every((option) => selectedSet.has(option.value));

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-invalid={invalid || undefined}
          onKeyDown={onKeyDown}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-md border bg-surface py-2 text-left',
            triggerSizeClass,
            'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-disabled',
            invalid ? 'border-danger' : 'border-border hover:border-border-strong',
            className,
          )}
          {...aria}
        >
          <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
            {selectedOptions.length === 0 ? (
              <span className="truncate text-fg-tertiary">{placeholder}</span>
            ) : selectedOptions.length > maxChips ? (
              <span className="truncate text-fg">
                {summaryLabel?.(selectedOptions.length) ?? selectedOptions.length}
              </span>
            ) : (
              selectedOptions.map((option) => (
                <span
                  key={String(option.value)}
                  className="flex max-w-full shrink-0 items-center gap-1 rounded-sm bg-surface-sunken px-2 py-0.5 text-body-sm"
                >
                  <span className="truncate">{option.label}</span>
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={removeLabel}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onChange(value.filter((current) => current !== option.value));
                    }}
                    className="rounded-sm p-0.5 text-fg-tertiary hover:text-fg"
                  >
                    <X className="h-3 w-3" />
                  </span>
                </span>
              ))
            )}
          </span>

          <ChevronDown
            aria-hidden
            className={cn('h-4 w-4 shrink-0 text-fg-tertiary transition-transform', open && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        popoverPortal={popoverPortal}
        role="presentation"
        className="flex max-h-[min(22rem,var(--radix-popover-content-available-height))] flex-col p-0"
      >
        {searchable ? (
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search aria-hidden className="h-4 w-4 shrink-0 text-fg-tertiary" />
            <input
              autoFocus
              value={search}
              placeholder={searchPlaceholder}
              onChange={(event) => {
                setSearch(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onKeyDown}
              className="w-full bg-transparent text-body outline-none placeholder:text-fg-tertiary"
            />
          </div>
        ) : null}

        <div id={listId} role="listbox" aria-multiselectable className="flex-1 overflow-y-auto p-1">
          {visible.length === 0 ? (
            <p className="px-3 py-6 text-center text-body-sm text-fg-secondary">
              {emptyMessage}
            </p>
          ) : (
            visible.map((option, index) => {
              const isSelected = selectedSet.has(option.value);
              return (
                <div
                  key={String(option.value)}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled || undefined}
                  onPointerEnter={() => setActiveIndex(index)}
                  onClick={() => toggle(option)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-body',
                    index === activeIndex && 'bg-surface-sunken',
                    option.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                      isSelected ? 'border-brand bg-brand text-on-brand' : 'border-border-strong',
                    )}
                  >
                    {isSelected ? <Check className="h-3 w-3" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-fg">{option.label}</span>
                    {option.description ? (
                      <span className="block truncate text-caption text-fg-secondary">
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {selectAllLabel || clearAllLabel ? (
          <div className="flex items-center justify-end gap-2 border-t border-border px-2 py-1.5">
            {clearAllLabel ? (
              <button
                type="button"
                onClick={() => onChange([])}
                disabled={value.length === 0}
                className="rounded px-2 py-1 text-body-sm text-fg-secondary hover:text-fg disabled:opacity-40"
              >
                {clearAllLabel}
              </button>
            ) : null}
            {selectAllLabel ? (
              <button
                type="button"
                onClick={() =>
                  onChange(
                    Array.from(
                      new Set([...value, ...selectableVisible.map((option) => option.value)]),
                    ),
                  )
                }
                disabled={allVisibleSelected}
                className="rounded px-2 py-1 text-body-sm font-medium text-brand disabled:opacity-40"
              >
                {selectAllLabel}
              </button>
            ) : null}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
